import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { blogPosts } from "@/data/blogPosts";
import { ExternalLink, FileText, Globe, MapPin, BookOpen } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { generateTourSlug } from "@/lib/tourHelpers";

const SitemapView = () => {
  const { t } = useTranslation();
  const [places, setPlaces] = useState<Array<{ Name: string }>>([]);
  const [tours, setTours] = useState<Array<{ id: string; name: string; style: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [toursLoading, setToursLoading] = useState(true);

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const { data, error } = await supabase
          .from('podcasts')
          .select('Name')
          .not('Name', 'is', null)
          .neq('Name', '')
          .order('Name', { ascending: true })
          .limit(1000);
        
        if (error) throw error;
        setPlaces(data || []);
      } catch (error) {
        console.error('Error fetching places:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchTours = async () => {
      try {
        const { data, error } = await supabase
          .from('city_tours')
          .select('id, "Nom ville", style')
          .eq('is_public', true)
          .order('"Nom ville"', { ascending: true });
        
        if (error) throw error;
        // Map the data to handle the space in column name
        const mappedData = (data || []).map(tour => ({
          id: tour.id,
          name: tour['Nom ville'] as string,
          style: tour.style as string
        }));
        setTours(mappedData);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setToursLoading(false);
      }
    };

    fetchPlaces();
    fetchTours();
  }, []);

  const staticPages = [
    { path: '/', name: 'Home', icon: Globe },
    { path: '/map', name: 'Map', icon: MapPin },
    { path: '/tours', name: 'Tours', icon: MapPin },
    { path: '/blog', name: 'Blog', icon: BookOpen },
    { path: '/auth', name: 'Authentication', icon: FileText },
    { path: '/profil', name: 'Profile', icon: FileText },
    { path: '/mentions-legales', name: 'Legal Notice', icon: FileText },
  ];

  const blogPages = blogPosts.filter(post => post.published).map(post => ({
    path: `/blog/${post.slug}`,
    name: post.title,
    date: post.date,
  }));

  const totalPages = staticPages.length + blogPages.length + places.length + tours.length;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Sitemap</h1>
          <p className="text-muted-foreground">
            All available pages on DiskOver ({totalPages} total pages)
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Static Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                {t('staticPages')}
              </CardTitle>
              <CardDescription>{staticPages.length} main pages</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {staticPages.map((page) => (
                  <li key={page.path}>
                    <Link 
                      to={page.path}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <page.icon className="w-4 h-4" />
                      {page.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Blog Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {t('blogPosts')}
              </CardTitle>
              <CardDescription>{blogPages.length} published articles</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {blogPages.map((page) => (
                  <li key={page.path}>
                    <Link 
                      to={page.path}
                      className="block text-sm hover:text-primary transition-colors"
                    >
                      <div className="font-medium line-clamp-2">{page.name}</div>
                      <div className="text-xs text-muted-foreground">{page.date}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Tours Pages */}
          <Card className="md:col-span-2 lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {t('toursPage')}
              </CardTitle>
              <CardDescription>
                {toursLoading ? t('loadingDiscoveries') : `${tours.length} ${t('toursAvailable')}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {toursLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {tours.map((tour) => (
                    <Link
                      key={tour.id}
                      to={`/tours/${generateTourSlug(tour.style, tour.name)}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="line-clamp-1">{tour.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Places Pages */}
          <Card className="md:col-span-2 lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {t('placesPage')}
              </CardTitle>
              <CardDescription>
                {loading ? t('loadingDiscoveries') : `${places.length} ${t('placesDisplayed')}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {places.map((place) => (
                    <Link
                      key={place.Name}
                      to={`/place/${encodeURIComponent(place.Name)}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="line-clamp-1">{place.Name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Links to XML Sitemaps */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>XML Sitemaps</CardTitle>
            <CardDescription>For search engines</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <a
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Sitemap Index
              </a>
              <a
                href="/sitemap-en.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Sitemap EN
              </a>
              <a
                href="/sitemap-fr.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Sitemap FR
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SitemapView;
